import { TransactionGroupDTO } from 'pages/users/types';

// RLPR-20420 bank verification of pending ACH deposits through Stripe Financial Connections: shapes returned by
// the admin API (mirrors the rsm-integrations-sdk records AchBankVerificationResponse, AchTransactionsCheckResponse,
// BankTransaction, BankVerificationState and ReleaseAchFundsResponse; verdicts and bank statuses are the
// serialized names of the AchTransactionsVerdict and BankTransactionStatus enums)

export interface AchAllocation {
  baseAmountCents: number;
  instantAmountCents: number;
  pendingAmountCents: number;
}

// stored on the deposit (transaction group details.bankVerification) by billing
export interface BankVerificationState {
  requestedAt?: string | null;
  requestedBy?: string | null;
  emailSent?: boolean;
  lastCheckedAt?: string | null;
  lastCheckedBy?: string | null;
  // POSTED, PENDING, NOT_FOUND, REFRESH_PENDING or NO_TRANSACTIONS_ACCESS
  lastVerdict?: string | null;
  lastFinancialConnectionsAccount?: string | null;
  lastTransactionId?: string | null;
  lastTransactionStatus?: string | null;
  lastTransactionAmountCents?: number | null;
  lastTransactionPostedAt?: string | null;
  // test-mode amount override the last check matched against, null for a real check
  lastAmountOverrideCents?: number | null;
}

export interface AchBankVerificationResponse {
  paymentIntent: string;
  portalUrl: string;
  emailSent: boolean;
  requestedBy?: string | null;
  requestedAt?: string | null;
}

export interface BankTransaction {
  id: string;
  // signed cents as reported by the bank, debits are negative
  amountCents: number;
  currency?: string | null;
  description?: string | null;
  // pending, posted or void
  status: string;
  transactedAt?: string | null;
  postedAt?: string | null;
  voidAt?: string | null;
  amountMatches: boolean;
}

export interface AchTransactionsCheckResponse {
  paymentIntent: string;
  paymentIntentStatus?: string | null;
  depositAmountCents: number;
  // the amount the candidates were matched against: the deposit amount, or the test-mode override
  matchedAmountCents: number;
  // billing talks to the Stripe test account, amount overrides are allowed
  testMode: boolean;
  pendingAmountCents: number;
  bankName?: string | null;
  lastDigits?: string | null;
  financialConnectionsAccount?: string | null;
  accountStatus?: string | null;
  refreshRequested: boolean;
  refreshStatus?: string | null;
  lastAttemptedAt?: string | null;
  nextRefreshAvailableAt?: string | null;
  // POSTED, PENDING, NOT_FOUND, REFRESH_PENDING or NO_TRANSACTIONS_ACCESS
  verdict: string;
  bestMatch?: BankTransaction | null;
  candidates: BankTransaction[];
  availableBalanceCents?: number | null;
  currentBalanceCents?: number | null;
  balanceAsOf?: string | null;
  checkedAt?: string | null;
  checkedBy?: string | null;
}

export interface ReleaseAchFundsResponse {
  paymentIntent: string;
  groupTransactionId?: string | null;
  releasedAmountCents: number;
  allocation: AchAllocation;
  bankTransactionId?: string | null;
}

export const VERDICT_POSTED = 'POSTED';
export const VERDICT_PENDING = 'PENDING';
export const VERDICT_NOT_FOUND = 'NOT_FOUND';
export const VERDICT_REFRESH_PENDING = 'REFRESH_PENDING';
export const VERDICT_NO_TRANSACTIONS_ACCESS = 'NO_TRANSACTIONS_ACCESS';

export interface AchTransactionRecord extends TransactionGroupDTO {
  details?: {
    paymentIntentId?: string;
    achAllocation?: AchAllocation;
    bankVerification?: BankVerificationState;
  };
}

export interface AchActionProps {
  record: AchTransactionRecord;
  hideAction?: boolean;
}

export interface BankVerificationFormValues {
  sendEmail: boolean;
}

export interface ReleaseAchFundsFormValues {
  amount: string;
  reason: string;
}

export type ReleaseAchFundsFormErrors = Partial<Record<keyof ReleaseAchFundsFormValues, string>>;
